import BigNumber from 'bignumber.js';
import { match } from 'ts-pattern';
import { formatUnits, zeroAddress } from 'viem';

import type { SwidgeGetOutput } from '@infinex/actions';
import type {
  AssetId,
  AssetMetadataV2,
  NftDisplayMetadataMap,
  NftMetadata,
  ChainAsset,
} from '@infinex/asset-config';
import { getChainAsset } from '@infinex/asset-config';
import type { ChainKey } from '@infinex/chain-config';
import type { SonarSalePurchase } from '@infinex/main-service-contract/v2/client/activity';
import type { GasActivity } from '@infinex/main-service-contract/v2/client/gas';
import type { InxActivityItem, InxActivityStatus } from '@infinex/types';

import { SwidgeProviderExplorerLink } from '../..';
import type { PatronPurchaseReceipt, Withdrawal } from '../../../api';
import { getSonarPurchaseStatus } from '../../../api/sonar';
import type {
  AirdropActivity,
  AirdropAllocationWithKey,
  ExternalActionActivity,
  PatronPurchaseActivity,
  PatronRedemptionActivity,
  SendActivity,
  SonarSalePurchaseActivity,
  SwidgeActivity,
  CashbackClaimActivity,
  CashbackClaim,
  GasActivity as GasFEActivity,
  InxClaimActivity,
  InxEarlyUnlockActivity,
  InxVoucherRedemptionActivity,
} from '../../../types';
import { ActivityType, ActivityStatus } from '../../../types';
import {
  formatTokenAmount,
  mapGenericStatus,
  mapSwidgeStatusToActivityStatus,
} from '../../../utils';
import { isTerminalStatus, mapSwidgeStatusToClientStatus } from '../../swidge';

export const mapToAirdropActivity = (
  a: AirdropAllocationWithKey
): AirdropActivity => ({
  id: a.id,
  type: ActivityType.AIRDROP_CLAIM, // TBB: To be deleted
  status: mapGenericStatus(a.claimStatus),
  title: a.title,
  amount: a.amount,
  amountUsdFormatted: a.claimedTotalValueUSD || undefined,
  chain: a.chain || undefined,
  assetId: a.allocationType === 'tokens' ? a.assetId! : undefined,
  pointType: a.allocationType === 'points' ? a.pointType! : undefined,
  denomination:
    a.allocationType === 'denomination' ? a.denomination! : undefined,
  timestamp: new Date(a.claimCompletedAt ?? a.claimStartedAt ?? a.createdAt),
  txnHash: a.claimTransactionSignature ?? undefined,
  decimals: a.decimals,
  tag: a.key,
  receiverAddress: a.claimedToAddress || undefined,
});

export const mapCashbackClaimToAirdropActivity = (
  a: CashbackClaim
): CashbackClaimActivity => ({
  id: a.id,
  type: ActivityType.CASHBACK_CLAIM,
  status: mapGenericStatus(a.status),
  amount: a.amount,
  amountUi: a.amountUi,
  amountUsdFormatted: a.amountUsd ?? undefined,
  chain: a.chain,
  assetAddress: a.tokenAddress,
  decimals: a.decimals,
  isInReview: a.status === 'review',
  requestId: a.requestId,
  timestamp: a.createdAt,
  txnHash: a.txHash ?? undefined,
});

export const takeFirstNftMetadata = (
  nftMetadata?: NftDisplayMetadataMap
): NftMetadata | undefined => {
  if (!nftMetadata) return undefined;
  const firstIdentifier = nftMetadata?.identifiers?.[0] ?? '';
  return {
    identifier: firstIdentifier,
    ...(nftMetadata?.metadataMap?.[firstIdentifier] ?? {}),
  };
};

export const getAssetIdFromMetadata = (
  assetMetadata?: AssetMetadataV2 | null
): AssetId | undefined => {
  if (!assetMetadata || !('assetId' in assetMetadata.groupId)) {
    return undefined;
  }

  return assetMetadata.groupId.assetId as AssetId;
};

export const mapToSendActivity = (
  w: Withdrawal,
  assetMetadata?: AssetMetadataV2 | null
): SendActivity => ({
  id: w.id,
  type: ActivityType.SEND,
  status: mapGenericStatus(w.status),
  toAddress: w.withdrawalAddress ?? '',
  fromAddress: w.accountAddress ?? '',
  amount: new BigNumber(w.amountBigint),
  decimals: w.assetDecimals,
  amountUsd: w.amountUsd ? Number(w.amountUsd) : null,
  assetId: getAssetIdFromMetadata(assetMetadata),
  assetAddress: w.tokenAddress,
  nftMetadata: w.nftMetadata as NftDisplayMetadataMap,
  chain: w.chain,
  timestamp: new Date(w.createdAt ?? Date.now()),
  txnHash: w.transactionHash ?? undefined,
  rawResponse: w,
  isMove: w.isMove,
});

export const mapFromReceiptToPatronPurchaseActivity = (
  p: PatronPurchaseReceipt,
  officialChainAssets: (ChainAsset & { assetId: AssetId })[]
): PatronRedemptionActivity => ({
  id: p.id,
  type: ActivityType.PATRON_REDEMPTION,
  status: mapGenericStatus(p.status),
  assetValue: Number(
    formatUnits(
      BigInt(p.value),
      getChainAsset(officialChainAssets, {
        chain: p.chain,
        assetId: p.assetId as AssetId,
      })?.decimals ?? 18
    )
  ),
  assetId: p.assetId as AssetId,
  quantity: p.quantity,
  tier: p.tier,
  wave: p.wave,
  chain: p.chain,
  timestamp: new Date(p.createdAt ?? Date.now()),
  burnTxnHash: p.transactionHash ?? undefined,
});

export const mapFromPatronReceiptToPatronPurchaseActivity = (
  p: PatronPurchaseReceipt
): PatronPurchaseActivity => ({
  id: p.id,
  type: ActivityType.PATRON_PURCHASE,
  status: mapGenericStatus(p.status),
  assetValue: p.value,
  rateToUsd: p.rateToUsd,
  assetId: p.assetId as AssetId,
  quantity: p.quantity,
  tier: p.tier,
  wave: p.wave,
  chain: p.chain,
  timestamp: new Date(p.createdAt ?? Date.now()),
  transactionHash: p.transactionHash ?? undefined,
});

export const mapToSwidgeActivity = (
  swidgeGetResponse: SwidgeGetOutput,
  {
    fromAssetMetadata,
    toAssetMetadata,
  }: {
    fromAssetMetadata?: AssetMetadataV2 | null;
    toAssetMetadata?: AssetMetadataV2 | null;
  }
): SwidgeActivity => {
  const order = swidgeGetResponse;

  if (!order) {
    throw new Error(`No action or order found for swidge activity`);
  }

  const toAssetAddress =
    order.quote.to.assetAddress === zeroAddress
      ? 'native'
      : order.quote.to.assetAddress;

  const explorerLink = (
    <SwidgeProviderExplorerLink
      href={order.providerExplorerLink}
      providerName={order.providerName}
      dimOnUndefined={isTerminalStatus(
        mapSwidgeStatusToClientStatus(order.status)
      )}
    />
  );

  return {
    id: order?.id ?? '',
    type: ActivityType.SWIDGE,
    status: mapSwidgeStatusToActivityStatus(order.status),
    fromAssetId: getAssetIdFromMetadata(fromAssetMetadata),
    fromAssetAddress: order.quote.from.assetAddress,
    toAssetId: getAssetIdFromMetadata(toAssetMetadata),
    toAssetAddress: toAssetAddress,
    fromAmount: formatTokenAmount(
      order.quote.from.amount.amount,
      order.quote.from.amount.decimals
    ).toString(),
    quotedToAmount: formatTokenAmount(
      order.quote.to.amount.amount,
      order.quote.to.amount.decimals
    ).toString(),
    fromChain: order.quote.from.chain,
    toChain: order.quote.to.chain,
    timestamp: new Date(order?.requestedAt ?? Date.now()),
    explorerLink,
    rawResponse: swidgeGetResponse,
  };
};

export const mapToExternalActionActivity = (
  action: any, // External app action from DB
  assetMetadata?: AssetMetadataV2 | null
): ExternalActionActivity => {
  // Map status from DB status to ActivityStatus
  const mapExternalActionStatus = (status: string): ActivityStatus => {
    switch (status) {
      case 'pending':
      case 'submitted':
        return ActivityStatus.CREATED;
      case 'executing':
        return ActivityStatus.PROCESSING;
      case 'success':
        return ActivityStatus.SUCCESS;
      case 'error':
        return ActivityStatus.ERROR;
      case 'requires_review':
        return ActivityStatus.REVIEW;
      default:
        return ActivityStatus.CREATED;
    }
  };

  const decimals =
    assetMetadata?.members[action.chain as ChainKey]?.decimals ?? 6; // Default to 6 for USDC

  // The amount from the backend is already in the smallest unit (e.g., "12350000" for 12.35 USDC with 6 decimals)
  // Just pass it through as a BigNumber like other activity types
  const amount = new BigNumber(action.amount || '0');

  return {
    id: action.id,
    type: ActivityType.EXTERNAL_APP_ACTION,
    action: action.action as 'deposit' | 'withdraw',
    protocol: action.protocol,
    chain: action.chain,
    assetId: getAssetIdFromMetadata(assetMetadata),
    assetAddress: action.assetAddress,
    amount: amount.toString(),
    amountUsd: action.amountUsd || undefined, // Keep as bigint string from DB
    decimals,
    txnHash: action.txHash,
    walletAddress: action.walletAddress,
    status: mapExternalActionStatus(action.status),
    timestamp: new Date(action.createdAt),
  };
};

export const mapGasActivity = (activity: GasActivity): GasFEActivity => {
  const status = match(activity.status)
    .with('Success', () => ActivityStatus.SUCCESS)
    .exhaustive();
  return {
    ...activity,
    type: ActivityType.GAS,
    status,
  };
};

export const mapSonarPurchaseToActivity = (
  p: SonarSalePurchase,
  walletAddress: string
): SonarSalePurchaseActivity => {
  const deriveStatus = (): ActivityStatus => {
    switch (getSonarPurchaseStatus(p)) {
      case 'in-progress':
        return ActivityStatus.PROCESSING;
      case 'successful':
        return ActivityStatus.SUCCESS;
      case 'failed':
        return ActivityStatus.ERROR;
      default:
        return ActivityStatus.ERROR;
    }
  };

  return {
    id: p.id,
    type: ActivityType.SONAR_SALE_PURCHASE,
    status: deriveStatus(),
    decimalAmountUsdc: p.decimalAmountUsdc,
    walletAddress,
    txHash: p.txHash,
    chain: 'base',
    timestamp: new Date(p.createdAtTms),
  };
};

/**
 * Maps INX activity status to frontend ActivityStatus.
 */
const mapInxActivityStatus = (status: InxActivityStatus): ActivityStatus => {
  switch (status) {
    case 'created':
      return ActivityStatus.CREATED;
    case 'pending':
      return ActivityStatus.PROCESSING;
    case 'completed':
      return ActivityStatus.SUCCESS;
    case 'failed':
      return ActivityStatus.ERROR;
    case 'review':
      return ActivityStatus.REVIEW;
    default:
      return ActivityStatus.CREATED;
  }
};

/**
 * Maps an INX activity item from the backend to the appropriate frontend activity type.
 *
 * @param item - The INX activity item from getActionHistory endpoint
 * @returns The mapped frontend activity (InxClaimActivity, InxEarlyUnlockActivity, or InxVoucherRedemptionActivity)
 */
export const mapInxActivityToActivity = (
  item: InxActivityItem
): InxClaimActivity | InxEarlyUnlockActivity | InxVoucherRedemptionActivity => {
  const baseActivity = {
    id: item.id,
    status: mapInxActivityStatus(item.status),
    timestamp: new Date(item.createdAt),
    chain: item.chain,
    txHash: item.txHash,
    walletAddress: item.walletAddress,
    decimalInxAmount: item.decimalInxAmount,
  };

  switch (item.type) {
    case 'magna_claim':
      return {
        ...baseActivity,
        type: ActivityType.INX_CLAIM,
        categoryKey: item.source,
      };

    case 'sonar_early_unlock':
      return {
        ...baseActivity,
        type: ActivityType.INX_EARLY_UNLOCK,
        decimalPaymentAmount: item.decimalPaymentAmount,
        strikePrice: item.strikePrice,
        decimalPrepaidAmount: item.decimalPrepaidAmount,
        treasuryAddress: item.treasuryAddress,
        amountUsd: item.decimalInxUsdValue,
      };

    case 'voucher_redemption':
      return {
        ...baseActivity,
        type: ActivityType.INX_VOUCHER_REDEMPTION,
        decimalPaymentAmount: item.decimalPaymentAmount,
        strikePrice: item.strikePrice,
        denominationId: item.denominationId,
        treasuryAddress: item.treasuryAddress,
        amountUsd: item.decimalInxUsdValue,
      };
  }
};
